import { useState } from "react";
import {
  ApiError,
  approveDetermination,
  type ApprovalOutcome,
  type ApprovalState,
  type Determination,
} from "../api/client";
import { Callout } from "../components/Callout";
import { idempotencyKey } from "../api/idempotency";

/**
 * Screens 35, 37 and 38 — the Step 2 approval card and its dialog.
 *
 * **Two parties, two decisions, and an absent one reads as pending.** Blank reads as *not
 * applicable*, and both approvals always apply: the Audit Plan is not generated until both
 * are recorded, and neither alone releases it (assertion 1).
 *
 * **The dialog restates what is being approved before it commits.** Screen 37 shows the
 * clause groups overridden and the agreed duration against the calculated base, because an
 * approval is recorded against a snapshot — and the figure it carries is sent back, so a
 * dialog left open while the certification body applied an adjustment is refused rather
 * than approving the figure it was still showing.
 *
 * **Which approval this is comes from the role, on the server.** There is no party to pick
 * here, and that is the point: a party the client chose would let one role record the
 * other's approval.
 */
export function ApprovalCard({
  auditId,
  approvals,
  determination,
  onApproved,
}: {
  auditId: string;
  approvals: ApprovalState[];
  determination: Determination;
  /**
   * The approval landed, and the server's answer travels with it. `plan_generated` says
   * whether *this* approval was the second one and released the plan — a fact the route
   * returns rather than one the screen should infer, since a replayed idempotency key
   * records nothing and reports `false` while the state still reads `plan_generated`.
   */
  onApproved: (outcome: ApprovalOutcome) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mine = approvals.find((entry) => entry.mine);
  const both = approvals.every((entry) => entry.approved);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await approveDetermination(
        auditId,
        idempotencyKey(),
        determination.agreed_stage1_hours,
      );
      setOpen(false);
      onApproved(outcome);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "The approval could not be recorded.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>Approval</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        Both the certification body and the lead auditor must approve. The Audit Plan is not
        generated until both approvals are recorded.
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid2">
        {approvals.map((entry) => (
          <div className="plan-ed" key={entry.party}>
            <div className="eyebrow">
              {entry.party === "cb" ? "Certification body" : "Lead auditor"}
            </div>
            {entry.approved ? (
              <>
                <span className="pill p-ok">Approved</span>
                <div className="sub" style={{ marginTop: "6px" }}>
                  {entry.approved_by ?? "—"} · {stamp(entry.approved_at)}
                </div>
              </>
            ) : (
              <>
                <span className="pill p-wait">Pending</span>
                {entry.mine ? (
                  <div style={{ marginTop: "10px" }}>
                    <button type="button" className="btn" onClick={() => setOpen(true)}>
                      {entry.party === "cb"
                        ? "Approve as certification body"
                        : "Approve as lead auditor"}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>

      {both ? (
        <div style={{ marginTop: "16px" }}>
          <Callout tone="info" title="Both approvals recorded.">
            The Audit Plan has been generated from the confirmed scope split and the agreed
            duration, and is awaiting lead auditor review at Step 3.
          </Callout>
        </div>
      ) : null}

      {open && mine ? (
        <Scrim onDismiss={() => setOpen(false)}>
          <h2>Approve scope and duration</h2>
          <p className="sub" style={{ marginBottom: "14px" }}>
            You are approving the confirmed scope split and the final agreed audit duration.
          </p>

          <div className="plan-ed" style={{ marginBottom: "14px" }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span>Scope</span>
              <b>{overridden(determination)} clause group(s) overridden</b>
            </div>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span>Agreed duration</span>
              <b className="mono">
                {determination.agreed_stage1_hours} h Stage 1 (
                {determination.agreed_percent_of_calculated}% of the calculated
                allocation)
              </b>
            </div>
          </div>

          {determination.below_floor ? (
            <div className="warnbar">
              {determination.threshold_message} Approving records this departure prominently
              in the audit file. {determination.threshold_clause}
            </div>
          ) : null}

          <p className="sub">
            Once the other party also approves, the Audit Plan is generated. Neither
            approval alone releases it.
          </p>

          <div className="mfoot">
            <button type="button" className="btn sec" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void confirm()}>
              {busy ? "Recording…" : "Confirm approval"}
            </button>
          </div>
        </Scrim>
      ) : null}
    </div>
  );
}

/**
 * Screen 36 — the plan that does not exist yet.
 *
 * An empty state rather than an error, because nothing is wrong: the audit exists, the
 * request was correct, and what the reader needs is which approval is outstanding.
 */
export function PlanBlocked({
  awaiting,
  reason,
  onBack,
}: {
  awaiting: string[];
  reason: string | null;
  onBack: () => void;
}) {
  return (
    <div className="empty">
      <h1>Plan not yet generated</h1>
      <p>{reason ?? "The Audit Plan is generated only after both Step 2 approvals."}</p>
      <p className="sub">
        Waiting on:{" "}
        {awaiting
          .map((party) => (party === "cb" ? "the certification body" : "the lead auditor"))
          .join(" and ")}
        .
      </p>
      <button type="button" className="btn" onClick={onBack}>
        Back to Step 2
      </button>
    </div>
  );
}

function overridden(determination: Determination): number {
  return determination.sections.filter((section) => section.override_reason).length;
}

function Scrim({
  children,
  onDismiss,
}: {
  children: React.ReactNode;
  onDismiss: () => void;
}) {
  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}
